import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import * as _ from 'lodash-es';
import {Subject} from 'rxjs';
import {distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {AutocompleteListComponent, DisplayVariant} from '../../autocomplete/autocomplete-list.component';
import {Deliverable} from '../../dex/dex-streaming/types/deliverable';


interface AutocompleteHighlightedItem {
    symbol: SafeHtml;
}

interface DisplayDeliverableVariant extends DisplayVariant<Deliverable> {
    variant: Deliverable;
    highlighted: AutocompleteHighlightedItem;
}

@Component({
    selector:    'ul[deliverableAutocompleteList]',
    templateUrl: './deliverable-autocomplete-list.component.html',
    styleUrls:   ['./deliverable-autocomplete-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone:  false
})
export class DeliverableAutocompleteListComponent extends AutocompleteListComponent<Deliverable, DisplayDeliverableVariant>
    implements OnInit {

    @Input()
    public term$: Subject<string>;

    private _autocompleteItems: Deliverable[];

    private readonly domSanitizer = this.injector.get(DomSanitizer);

    private term: string;

    public override ngOnInit(): void {
        super.ngOnInit();

        this.listenToTerm();
    }

    public override setVariants(variants: Deliverable[], autocompleteItems?: Deliverable[]): void {
        if (this._variants !== variants || this._autocompleteItems !== autocompleteItems) {
            this._variants = variants;
            this._autocompleteItems = autocompleteItems;
            this.updateVariants();
        }
    }

    protected override updateVariants(): void {
        this.displayVariants = this._autocompleteItems.map<DisplayDeliverableVariant>(
            item => this.createDisplayVariant(item)
        );

        this.selectedVariant = _.head(this._variants);

        if (this.term) {
            this.updateDisplayVariantsByTerm(this.term);
        }

        this.updateIsEmpty();
    }

    protected createDisplayVariant(item: Deliverable): DisplayDeliverableVariant {
        return {
            variant: item,

            highlighted: !this.term
                         ? this.createHighlightedItem(item, '') // TODO
                         : null /** To be created by {@link updateDisplayVariantsByTerm} */
        };
    }

    private createHighlightedItem(d: Deliverable, term: string): AutocompleteHighlightedItem {
        const symbol = this.getHighlightedItemPart(d, term);

        return {symbol};
    }

    private getTermRegExp(term: string): RegExp {
        const termEscapedWords = term
            .split(/\s+/)
            .map(_.escapeRegExp);

        /**
         * [TRAD-195] Use start-with in security search on UI
         */
        const regExpStr = `\\b(${termEscapedWords.join('|')})`;

        return new RegExp(regExpStr, 'gi');
    }

    private highlightString(str: string, term: string): string {
        let resultStr = str;

        if (str && term) {
            const regExp = this.getTermRegExp(term);

            resultStr = str.replace(regExp, match => `<mark>${match}</mark>`);
        }

        return resultStr;
    }

    private getItemPartByAutocompleteKey(d: Deliverable): string {
        return d.symbol;
    }

    private getHighlightedItemPart(d: Deliverable, term: string): SafeHtml {
        const securityPart = this.getItemPartByAutocompleteKey(d);
        const markupStr = this.highlightString(securityPart, term);

        return this.domSanitizer.bypassSecurityTrustHtml(markupStr);
    }

    private listenToTerm(): void {
        this.term$.pipe(
            distinctUntilChanged(),
            tap(term => this.term = term),
            tap(term => this.updateDisplayVariantsByTerm(term)),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private updateDisplayVariantsByTerm(term: string): void {
        let firstMatchedVariant: DisplayDeliverableVariant;
        let isSelectedVariantMatched: boolean = true;

        this.displayVariants.forEach(displayVariant => {
            const searchTerm = this.isItemMatchedBySearchTerms(displayVariant.variant, term)
                ? term
                : '';

            displayVariant.highlighted = this.createHighlightedItem(
                displayVariant.variant,
                searchTerm
            );

            if (searchTerm) {
                firstMatchedVariant = firstMatchedVariant || displayVariant;
            } else {
                isSelectedVariantMatched = this.selectedVariant === displayVariant.variant
                    ? false
                    : isSelectedVariantMatched;
            }
        });

        if (!isSelectedVariantMatched && firstMatchedVariant) {
            this.selectedVariant = firstMatchedVariant.variant;
        }
    }

    private isItemMatchedBySearchTerms(d: Deliverable, term: string): boolean {
        const itemPart = this.getItemPartByAutocompleteKey(d);

        const regExp = this.getTermRegExp(term);

        return regExp.test(itemPart);
    }

}
